'use client'

import { useEffect } from 'react'
import { useLocale } from 'next-intl'
import type { ReactNode } from 'react'
import { usePathname } from 'next/navigation'
import { useAuth } from '@/hooks/useAuth'
import { routing, type AppLocale } from '@/i18n/routing'
import { useLocaleSettings } from '@/i18n/useLocaleSettings'
import { isEarlyLocalePage } from '@/i18n/surfaces'
import { persistLocaleChoice } from '@/lib/locale'
import type { LocaleSettings } from '@/i18n/overrides'

function isAppLocale(locale: string): locale is AppLocale {
  return (routing.locales as readonly string[]).includes(locale)
}

function stripCurrentLocalePath(pathname: string, locale: AppLocale): string {
  if (locale === routing.defaultLocale) return pathname

  const prefix = `/${locale}`
  if (pathname === prefix) return '/'
  if (pathname.startsWith(`${prefix}/`)) return pathname.slice(prefix.length) || '/'
  return pathname
}

function resolveAllowedFallbackLocale(settings: LocaleSettings): AppLocale {
  if (settings[routing.defaultLocale]?.enabled === true) return routing.defaultLocale

  return (
    (routing.locales as readonly AppLocale[]).find(locale => settings[locale]?.enabled === true) ??
    routing.defaultLocale
  )
}

function withLocalePrefix(pathname: string, locale: AppLocale): string {
  if (locale === routing.defaultLocale) return pathname
  return pathname === '/' ? `/${locale}` : `/${locale}${pathname}`
}

/**
 * Blocks direct visits to locales that are still hidden from non-editors.
 * The switcher only controls discovery; this gate enforces the same setting
 * when someone manually opens a locale-prefixed URL.
 */
export function LocaleAccessGate({ children }: { children: ReactNode }) {
  const locale = useLocale()
  const pathname = usePathname()
  const { editorLocales, isLoading: authLoading } = useAuth()
  const { settings, isLoading: settingsLoading } = useLocaleSettings({ editorLocales })

  const currentLocale = isAppLocale(locale) ? locale : routing.defaultLocale
  const isDefaultLocale = currentLocale === routing.defaultLocale
  const localeEnabled = settings[currentLocale]?.enabled === true
  const hasLocaleGrant = editorLocales.includes(currentLocale)
  const loading = authLoading || settingsLoading
  // Only block once we have a confirmed answer. Rendering optimistically while
  // settings load avoids a blank first paint on enabled locales; if it turns out
  // the locale is gated for this user, the effect below redirects them.
  const earlyPage = isEarlyLocalePage(currentLocale, stripCurrentLocalePath(pathname || '/', currentLocale))
  const shouldBlock = !loading && !isDefaultLocale && !hasLocaleGrant && !localeEnabled && !earlyPage

  useEffect(() => {
    if (!shouldBlock) return

    const fallbackLocale = resolveAllowedFallbackLocale(settings)
    const targetPath = withLocalePrefix(stripCurrentLocalePath(pathname || '/', currentLocale), fallbackLocale)
    const query = typeof window === 'undefined' ? '' : window.location.search.replace(/^\?/, '')
    persistLocaleChoice(fallbackLocale)
    window.location.replace(query ? `${targetPath}?${query}` : targetPath)
  }, [currentLocale, pathname, settings, shouldBlock])

  if (shouldBlock) return null

  return <>{children}</>
}
